import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { useLanguage } from '../../app/i18n';
import { NUMERIC_FONT } from '../../app/theme';
import { ConfirmSheet } from '../../components/ConfirmSheet';
import { formatMoney } from '../../components/format-money';

import { formatBpsPercent, formatWireDecimal } from './display';
import { HIGH_RISK_CONFIRM_PERCENT, type HighRiskConfirm } from './high-risk';

/** Props of {@link HighRiskConfirmSheet}. */
interface HighRiskConfirmSheetProps {
  /** What to confirm; `null` keeps the sheet closed. */
  readonly confirm: HighRiskConfirm | null;
  /**
   * The confirmed save is running: confirm shows progress, the sheet cannot be dismissed (P11 R9,
   * C3 R11).
   */
  readonly busy: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

const NUM = <Box component="strong" sx={{ fontFamily: NUMERIC_FONT }} />;

/**
 * The E7 sheet that asks the trader to confirm a high risk before it is saved (P11 R3–R9,
 * Proposal, to be validated, Q260). Built on C3's `ConfirmSheet` (C3 R10, R11): focus starts on
 * "Change the value", so nothing is ever confirmed by default (I7). Every number is the one the
 * trader typed or the preview computed, never a made-up amount (R8). In fixed lot it names the
 * example's instrument with the preview's own line (M2 R5, P9 R1–R3, Q32).
 */
export function HighRiskConfirmSheet({
  confirm,
  busy,
  onConfirm,
  onCancel,
}: HighRiskConfirmSheetProps): ReactElement {
  const { t } = useTranslation();
  const language = useLanguage();
  // Keep the last content while the sheet slides out, so it does not go blank as it closes.
  const [shown, setShown] = useState(confirm);
  if (confirm !== null && confirm !== shown) setShown(confirm);

  return (
    <ConfirmSheet
      open={confirm !== null}
      title={t('settings.highRisk.title')}
      confirmLabel={t('settings.highRisk.confirm')}
      cancelLabel={t('settings.highRisk.cancel')}
      tone="primary"
      icon={<WarningAmberRounded />}
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {shown !== null && (
        <Stack spacing={1}>
          <Typography>
            {shown.kind === 'fixedLot' ? (
              <Trans
                i18nKey="settings.highRisk.fixedLot"
                values={{
                  lots: formatWireDecimal(shown.lots, language),
                  pips: formatWireDecimal(shown.stopLossPips, language),
                  amount: formatMoney({ ...shown.estimatedLoss, language }),
                  share: formatBpsPercent(shown.equityShareBps, language),
                }}
                components={{ num: NUM }}
              />
            ) : shown.estimatedLoss === null ? (
              <Trans
                i18nKey="settings.highRisk.riskPercentNoAmount"
                values={{ percent: formatWireDecimal(shown.riskPercent, language) }}
                components={{ num: NUM }}
              />
            ) : (
              <Trans
                i18nKey="settings.highRisk.riskPercent"
                values={{
                  percent: formatWireDecimal(shown.riskPercent, language),
                  amount: formatMoney({ ...shown.estimatedLoss, language }),
                }}
                components={{ num: NUM }}
              />
            )}
          </Typography>
          {shown.kind === 'fixedLot' && (
            // M2 R5, P9 R1–R3: a fixed lot's amount depends on the instrument; say which one and why.
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {t(shown.reference.key, shown.reference.values)}
            </Typography>
          )}
          <Typography sx={{ color: 'text.secondary' }}>
            <Trans
              i18nKey="settings.highRisk.aboveThreshold"
              values={{ threshold: formatBpsPercent(HIGH_RISK_CONFIRM_PERCENT * 100, language) }}
              components={{ num: <Box component="span" sx={{ fontFamily: NUMERIC_FONT }} /> }}
            />
          </Typography>
        </Stack>
      )}
    </ConfirmSheet>
  );
}
